import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = ["new", "contacted", "closed"];
const statusColor = {
  new: "text-primary border-primary/40",
  contacted: "text-yellow-400 border-yellow-400/40",
  closed: "text-foreground/40 border-white/20",
};

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-card p-6" data-testid={testid}>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/40">{label}</p>
      <p className="mt-3 font-display font-black text-4xl">{value}</p>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [inq, st] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/stats"),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      load();
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/inquiries/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fuzepoint-inquiries.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return inquiries;
    const s = q.toLowerCase();
    return inquiries.filter((i) =>
      [i.firstName, i.lastName, i.email, i.company, i.serviceInterest]
        .join(" ")
        .toLowerCase()
        .includes(s)
    );
  }, [inquiries, q]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-background flex items-center justify-center font-mono text-foreground/40 text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-page">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 bg-primary block rotate-45" />
            <span className="font-display font-black uppercase tracking-tight">
              Fuze<span className="text-primary">Point</span> <span className="text-foreground/40 font-mono text-xs">/ Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="p-2 border border-white/10 hover:border-primary hover:text-primary transition-colors" title="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-primary hover:text-primary transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="Total Inquiries" value={stats.total} testid="stat-total" />
          <StatCard label="New" value={stats.new} testid="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} testid="stat-contacted" />
          <StatCard label="Closed" value={stats.closed} testid="stat-closed" />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/30" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, company…"
              data-testid="admin-search"
              className="w-full bg-card border border-white/10 pl-10 pr-4 py-2.5 text-sm focus:border-primary focus:outline-none transition-colors"
            />
          </div>
          <button onClick={exportCsv} data-testid="admin-export" className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white hover:bg-white hover:text-background transition-colors">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="inquiry-table">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-card font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/40 text-left">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Company</th>
                <th className="px-4 py-3 font-medium">Service</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-foreground/40 font-mono text-xs uppercase tracking-widest" data-testid="empty-state">No inquiries yet</td></tr>
              )}
              {filtered.map((i) => (
                <tr key={i.id} className="border-t border-white/10 hover:bg-card/60 transition-colors" data-testid={`inquiry-row-${i.id}`}>
                  <td className="px-4 py-3.5 font-mono text-[11px] text-foreground/50 whitespace-nowrap">{fmt(i.createdAt)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap font-medium">{i.firstName} {i.lastName}</td>
                  <td className="px-4 py-3.5 text-foreground/70">{i.email}</td>
                  <td className="px-4 py-3.5 text-foreground/60 hidden md:table-cell">{i.company || "—"}</td>
                  <td className="px-4 py-3.5 text-foreground/70">{i.serviceInterest || "—"}</td>
                  <td className="px-4 py-3.5">
                    <select
                      value={i.status}
                      onChange={(e) => changeStatus(i.id, e.target.value)}
                      data-testid={`status-select-${i.id}`}
                      className={`bg-background border px-2 py-1 font-mono text-[10px] uppercase tracking-widest focus:outline-none ${statusColor[i.status] || ""}`}
                    >
                      {STATUS.map((s) => (
                        <option key={s} value={s} className="bg-background text-foreground">{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
